import React, { useEffect, useState } from 'react';

import { useInView } from 'react-intersection-observer';
import { getTranslate } from 'react-localize-redux';
import { connect } from 'react-redux';
import { type RouteComponentProps } from 'react-router';

import ErrorBoundary from '../../../../layouts/Portal/ErrorBoundary';
import { permissionsSelector } from '../../../../selectors/permissions.selector';
import { getColumnsFlipdishStaffOrDefault } from '../../../Customers/helpers';
import { renderPeriodString } from '../../helpers';
import { GetTableDataOrders, getTableDataOrders } from '../../OrderReport.actions';
import { ordersMeta } from '../../OrdersReport/OrdersMetaData';
import { OrdersTableColumns, PeriodPresetEnum, PeriodProps } from '../../types';
import OrderReportTable from './OrderReportTable';

type CurrencyEnum = Required<Flipdish.OrderSummary>['Currency'];
type AppTypeEnum = Required<Flipdish.OrderSummary>['AppType'];
type Props = {
  currentPeriodFilter: PeriodProps;
  storeIds: number[] | string[];
  deliveryTypes: Array<'delivery' | 'collection'>;
  platforms: AppTypeEnum[];
  periodType: PeriodPresetEnum;
  vouchers: string; // we pass the query here &voucher
  userTimezone: string;
  currency?: CurrencyEnum;
  languageCode: string;
} & MappedState &
  MappedDispatch;

const columnLocalStorageKey = 'fd-reporting-orders-columns';
const rowsPerPageLocalStorageKey = 'fd-reporting-orders-rows-per-page';
const defaultTableColumns = ['orderId', 'requestedForTime', 'netAmount', 'orderState'];

const OrdersTable = (props: Props) => {
  const {
    getTableDataOrders,
    currentPeriodFilter,
    storeIds,
    deliveryTypes,
    platforms,
    periodType,
    AppId,
    vouchers,
    userTimezone,
    currency,
    languageCode,
    ordersTableData,
    ordersTablePagination,
    isFlipdishStaff,
  } = props;
  const [ref, inView] = useInView({ triggerOnce: true });
  const [firstRender, setFirstRender] = useState<boolean>(true);

  const [tableOrdersColumns, setTableOrdersColumns] = useState<OrdersTableColumns[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [filterState, setFilterState] = useState({
    currentPeriodFilter,
    periodType,
    storeIds,
    deliveryTypes,
    platforms,
    vouchers,
    orderBy: '',
    order: 'asc',
  });
  const [loading, setLoading] = useState<boolean | undefined>();
  const [hasNewColumns, setHasNewColumns] = useState(false);

  useEffect(() => {
    if (ordersTableData && firstRender) {
      setFirstRender(false);
    }
  }, [ordersTableData]);

  useEffect(() => {
    const lsColumns = localStorage.getItem(columnLocalStorageKey);
    let columns = ordersMeta;

    if (firstRender) {
      const defaultColumns = lsColumns ? JSON.parse(lsColumns) : defaultTableColumns;
      columns = columns.map((col) => {
        return {
          ...col,
          isVisible: defaultColumns.indexOf(col.columnName) !== -1,
        };
      });
    } else {
      // Handle refetch with new columns
      setHasNewColumns(true);
    }

    const colsByPermission = getColumnsFlipdishStaffOrDefault(columns, isFlipdishStaff);

    setTableOrdersColumns(colsByPermission);
  }, []);

  useEffect(() => {
    const rowsPerPage = localStorage.getItem(rowsPerPageLocalStorageKey);
    if (rowsPerPage) {
      setRowsPerPage(parseInt(rowsPerPage, 10));
    }
  }, []);

  useEffect(() => {
    if (inView && currency && !loading) {
      const storeFilter =
        Array.isArray(filterState.storeIds) && filterState.storeIds.length
          ? filterState.storeIds
          : currency;
      const params = {
        c: filterState.currentPeriodFilter,
        t: filterState.periodType,
        s: storeFilter as any,
        d: filterState.deliveryTypes,
        p: filterState.platforms,
        v: filterState.vouchers,
        o: filterState.order as 'asc' | 'desc',
        ob: filterState.orderBy,
        pg: page,
        r: rowsPerPage,
        l: languageCode,
      };
      getTableData(params);
    }
  }, [page, rowsPerPage, filterState, currency, hasNewColumns, languageCode]);

  useEffect(() => {
    if (inView && !loading) {
      setFilterState({
        ...filterState,
        currentPeriodFilter,
        periodType,
        storeIds,
        deliveryTypes,
        platforms,
        vouchers,
      });
      setPage(0);
    }
  }, [currentPeriodFilter, periodType, storeIds, deliveryTypes, platforms, vouchers, inView]);

  const getTableData = async (params: {
    c: PeriodProps;
    t: PeriodPresetEnum;
    s: number[] | string[];
    d: Array<'delivery' | 'collection'>;
    p: AppTypeEnum[];
    v: string;
    ob?: string;
    o?: 'asc' | 'desc';
    pg?: number;
    r?: number;
    csv?: boolean;
    l?: string;
  }) => {
    try {
      setLoading(true);
      const { c, t, s, d, p, v, ob, o, pg, r, csv, l } = params;
      const lsColumns = localStorage.getItem(columnLocalStorageKey);
      // Get visible columns from localstorage, only on mount
      const columnNames =
        !hasNewColumns && lsColumns
          ? JSON.parse(lsColumns)
          : tableOrdersColumns.filter((toc) => toc.isVisible).map((toc) => toc.columnName);
      await getTableDataOrders({
        Page: pg,
        PageSize: r,
        Period: c,
        PeriodType: t,
        StoreId: s,
        DeliveryType: d,
        Platform: p,
        VoucherCodes: v,
        IANATimezone: userTimezone,
        Columns: columnNames,
        SortKey: ob,
        SortDirection: o,
        ExportAsCSV: csv,
        LanguageCode: l,
      });
      setHasNewColumns(false);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      console.log(new Error(err));
    }
  };

  const onSort = (orderBy: string, order: 'asc' | 'desc') => {
    if (orderBy) {
      setFilterState({ ...filterState, orderBy, order });
      setPage(0);
    }
  };

  const onAppRowClick = ({
    row,
    history,
  }: {
    row: Reports.OrderListRow;
    history: RouteComponentProps['history'];
  }) => {
    history.push(`/${AppId}/orders/${row.orderId}`);
  };
  const downloadCSV = () => {
    const storeFilter =
      Array.isArray(filterState.storeIds) && filterState.storeIds.length
        ? filterState.storeIds
        : currency;
    getTableData({
      c: currentPeriodFilter,
      t: periodType,
      s: storeFilter as any,
      d: deliveryTypes,
      p: platforms,
      v: vouchers,
      csv: true,
      l: languageCode,
    });
  };

  return (
    <ErrorBoundary identifier="orders-table">
      <div ref={ref}>
        <OrderReportTable
          AppId={AppId}
          columnLocalStorageKey={columnLocalStorageKey}
          currency={currency}
          data={ordersTableData}
          defaultColumns={defaultTableColumns}
          downloadCSV={downloadCSV}
          inView={inView}
          languageCode={languageCode}
          loading={loading}
          metadata={tableOrdersColumns}
          onAppRowClick={onAppRowClick}
          onSort={onSort}
          page={page}
          pagination={ordersTablePagination}
          rowsPerPage={rowsPerPage}
          rowsPerPageLocalStorageKey={rowsPerPageLocalStorageKey}
          setPage={setPage}
          setRowsPerPage={setRowsPerPage}
          showColumnSelector
          subTitle={renderPeriodString(currentPeriodFilter)}
          title="Orders"
        />
      </div>
    </ErrorBoundary>
  );
};

type MappedState = ReturnType<ReturnType<typeof mapStateToPropsFactory>>;
const mapStateToPropsFactory = () => {
  const getPermissionsSelector = permissionsSelector.hasPermissionFactory(['FlipdishStaff']);
  return (state: AppState) => {
    const { ordersTableData, ordersTablePagination } = state.reports.orderReports;

    return {
      AppId: state.currentApp.AppId as string,
      translate: getTranslate(state),
      ordersTableData,
      ordersTablePagination,
      isFlipdishStaff: getPermissionsSelector(state),
    };
  };
};

type MappedDispatch = ReturnType<typeof mapDispatchToProps>;
const mapDispatchToProps = (dispatch: ThunkDispatch) => ({
  getTableDataOrders: ({
    Page,
    PageSize,
    Period,
    PeriodType,
    StoreId,
    DeliveryType,
    Platform,
    VoucherCodes,
    IANATimezone,
    Columns,
    SortKey,
    SortDirection,
    ExportAsCSV,
    LanguageCode,
  }: GetTableDataOrders) =>
    dispatch(
      getTableDataOrders({
        Page,
        PageSize,
        Period,
        PeriodType,
        StoreId,
        DeliveryType,
        Platform,
        VoucherCodes,
        IANATimezone,
        Columns,
        SortKey,
        SortDirection,
        ExportAsCSV,
        LanguageCode,
      })
    ),
});

export default connect(mapStateToPropsFactory, mapDispatchToProps)(OrdersTable);
